import React, { useRef, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Upload, Trash2, Pencil, ArrowLeft, ArrowRight, Check, Star, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageGalleryEditor — multi-image picker for admin product editors.
 *
 * Each tile shows the image with hover overlay containing:
 *   - Replace (pencil) — opens the system file picker, swaps THIS specific image
 *   - Delete (trash, requires 2-click confirmation, red on confirm-arm)
 *   - Move left / Move right
 *   - "Cover" badge on the first image — clicking the star on a non-first image
 *     promotes it to position 0 (so it becomes the cover)
 *
 * Props:
 *   value:   string[] — list of image URLs
 *   onChange: (next: string[]) => void
 *   testId:  string — prefix for data-testids
 *   max:     number — soft limit (just for the helper text); no hard cap
 */
const MAX_MB = 6;

export default function ImageGalleryEditor({ value = [], onChange, testId = "gallery", max = 20 }) {
    const [busy, setBusy] = useState(false);
    const [confirmIdx, setConfirmIdx] = useState(null); // index whose delete is armed
    const addFileRef = useRef(null);
    const replaceFileRef = useRef(null);
    const replaceIdxRef = useRef(null);

    const uploadFiles = async (files) => {
        const arr = Array.from(files || []);
        if (arr.length === 0) return [];
        const uploaded = [];
        for (const file of arr) {
            if (file.size > MAX_MB * 1024 * 1024) {
                toast.error(`${file.name}: must be under ${MAX_MB} MB`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", file);
                // eslint-disable-next-line no-await-in-loop
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push(`${BACKEND_URL}${data.url}`);
            } catch (e) {
                toast.error(`${file.name}: ${e.response?.data?.detail || "Upload failed"}`);
            }
        }
        return uploaded;
    };

    const handleAdd = async (e) => {
        // Snapshot to a real array FIRST — `e.target.files` is a live FileList,
        // and `e.target.value = ""` below would clear it before we got a chance
        // to read the picked files. Without this, picking files appeared to do
        // nothing (the early `!files.length` return triggered silently).
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setBusy(true);
        const uploaded = await uploadFiles(files);
        setBusy(false);
        if (uploaded.length > 0) {
            onChange([...(value || []), ...uploaded]);
            toast.success(uploaded.length === 1 ? "Image added" : `${uploaded.length} images added`);
        }
    };

    const handleReplace = async (e) => {
        // Same FileList-clearing gotcha — snapshot first.
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        const idx = replaceIdxRef.current;
        replaceIdxRef.current = null;
        const file = files[0];
        if (!file || idx == null) return;
        setBusy(true);
        const uploaded = await uploadFiles([file]);
        setBusy(false);
        if (uploaded.length === 1) {
            const next = [...value];
            next[idx] = uploaded[0];
            onChange(next);
            toast.success("Image replaced");
        }
    };

    const triggerReplace = (idx) => {
        replaceIdxRef.current = idx;
        replaceFileRef.current?.click();
    };

    const triggerDelete = (idx) => {
        if (confirmIdx !== idx) {
            setConfirmIdx(idx);
            setTimeout(() => setConfirmIdx((cur) => (cur === idx ? null : cur)), 4000);
            return;
        }
        const next = value.filter((_, i) => i !== idx);
        onChange(next);
        setConfirmIdx(null);
        toast.success("Image removed");
    };

    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= value.length) return;
        const next = [...value];
        [next[idx], next[j]] = [next[j], next[idx]];
        onChange(next);
        setConfirmIdx(null);
    };

    const setAsCover = (idx) => {
        if (idx === 0) return;
        const next = [...value];
        const [pulled] = next.splice(idx, 1);
        next.unshift(pulled);
        onChange(next);
        toast.success("Set as cover image");
    };

    return (
        <div data-testid={testId} className="space-y-3">
            {/* Hidden inputs */}
            <input ref={addFileRef} type="file" accept="image/*" multiple onChange={handleAdd} className="hidden" data-testid={`${testId}-add-file`} />
            <input ref={replaceFileRef} type="file" accept="image/*" onChange={handleReplace} className="hidden" data-testid={`${testId}-replace-file`} />

            {/* Add button */}
            <Button
                type="button"
                variant="outline"
                onClick={() => addFileRef.current?.click()}
                disabled={busy}
                className="rounded-none border-stone-400 w-full justify-start"
                data-testid={`${testId}-add`}
            >
                <Upload className="w-4 h-4 mr-2" />
                {busy ? "Uploading…" : `Add image${(value?.length || 0) ? "s" : ""} · pick multiple at once · up to 10+ per product · ≤ ${MAX_MB} MB each`}
            </Button>

            {value.length === 0 ? (
                <p className="text-xs text-stone-500 italic">No images yet — add one above. The first image becomes the cover.</p>
            ) : (
                <>
                    <p className="text-xs text-stone-500">
                        {value.length} image{value.length === 1 ? "" : "s"} · hover any tile to <strong>Replace</strong> / <strong>Delete</strong> / reorder · <span className="text-stone-600">add up to 10+ photos per product</span>
                        {value.length >= max && <span className="text-amber-700"> · {max}+ images may slow the product page on slow networks</span>}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {value.map((url, i) => {
                            const armed = confirmIdx === i;
                            return (
                                <div
                                    key={`${i}-${url.slice(-12)}`}
                                    className={`relative aspect-square border bg-stone-100 overflow-hidden group ${armed ? "border-red-600 ring-2 ring-red-300" : "border-stone-200"}`}
                                    onMouseLeave={() => confirmIdx === i && setConfirmIdx(null)}
                                    data-testid={`${testId}-tile-${i}`}
                                >
                                    <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />

                                    {/* Cover badge */}
                                    {i === 0 && (
                                        <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-sky-700 text-white text-[9px] uppercase tracking-widest flex items-center gap-1 pointer-events-none">
                                            <Star className="w-2.5 h-2.5" />Cover
                                        </div>
                                    )}

                                    {/* Confirm-delete banner */}
                                    {armed && (
                                        <div className="absolute inset-x-0 top-0 bg-red-700/90 text-white text-[10px] py-1 px-1.5 flex items-center justify-center gap-1 pointer-events-none">
                                            <AlertTriangle className="w-3 h-3" />Click delete again to confirm
                                        </div>
                                    )}

                                    {/* Hover overlay */}
                                    <div className={`absolute inset-0 bg-stone-900/55 flex flex-col items-center justify-center gap-1.5 transition-opacity ${armed ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}>
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => triggerReplace(i)}
                                                className="bg-white text-stone-900 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-sky-50 flex items-center gap-1"
                                                title="Replace this image"
                                                data-testid={`${testId}-replace-${i}`}
                                            >
                                                <Pencil className="w-3 h-3" />Replace
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => triggerDelete(i)}
                                                className={`px-2 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1 ${armed ? "bg-red-700 text-white" : "bg-white text-red-700 hover:bg-red-50"}`}
                                                title={armed ? "Click to confirm" : "Delete this image"}
                                                data-testid={`${testId}-delete-${i}`}
                                            >
                                                {armed ? <Check className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                                                {armed ? "Confirm" : "Delete"}
                                            </button>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => move(i, -1)}
                                                disabled={i === 0}
                                                className="bg-white/80 text-stone-700 w-7 h-6 grid place-items-center disabled:opacity-30"
                                                title="Move left"
                                                data-testid={`${testId}-left-${i}`}
                                            >
                                                <ArrowLeft className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => move(i, 1)}
                                                disabled={i === value.length - 1}
                                                className="bg-white/80 text-stone-700 w-7 h-6 grid place-items-center disabled:opacity-30"
                                                title="Move right"
                                                data-testid={`${testId}-right-${i}`}
                                            >
                                                <ArrowRight className="w-3 h-3" />
                                            </button>
                                            {i !== 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAsCover(i)}
                                                    className="bg-white/80 text-sky-700 w-7 h-6 grid place-items-center"
                                                    title="Set as cover"
                                                    data-testid={`${testId}-cover-${i}`}
                                                >
                                                    <Star className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
